/**
 * Small allocation-free math helpers shared by sensors/ and pose/.
 *
 * Quaternions are [x, y, z, w] (contracts.ts). All functions that produce a
 * quaternion or vector write into a caller-supplied `out` and return it.
 */

export type Quat = [number, number, number, number];
export type Vec3 = [number, number, number];
/** Read-only quaternion input (tuple, readonly array, Float64Array, ...). */
export type QuatLike = ArrayLike<number>;

export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;
const SIN45 = Math.SQRT1_2;

// ─── Angles ──────────────────────────────────────────────────────────────────

/** Wrap degrees into [0, 360). */
export function wrap360(deg: number): number {
  let d = deg % 360;
  if (d < 0) d += 360;
  if (d >= 360) d -= 360;
  return d + 0; // normalise -0
}

/** Wrap degrees into (-180, 180]. */
export function wrap180(deg: number): number {
  const d = wrap360(deg);
  return d > 180 ? d - 360 : d;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Heading (deg clockwise from north, [0, 360)) of a camera given its forward
 * vector f and screen-up vector u, both in an (east, north, up) basis.
 *
 * For |pitch| <= 45 deg this is exactly the azimuth of the horizontal
 * projection of f (independent of roll). Beyond that it blends smoothly
 * toward the azimuth of +u (looking down) or -u (looking up), so a device
 * lying flat reports the direction its screen top points to, and the value
 * stays continuous through the vertical.
 */
export function headingFromForwardUp(fE: number, fN: number, fU: number, uE: number, uN: number): number {
  const s = Math.abs(fU);
  let e = fE;
  let n = fN;
  if (s > SIN45) {
    const x = (s - SIN45) / (1 - SIN45);
    const k = fU < 0 ? x * x : -x * x;
    e += k * uE;
    n += k * uN;
  }
  if (e === 0 && n === 0) return 0;
  return wrap360(Math.atan2(e, n) * RAD2DEG);
}

// ─── Quaternions ─────────────────────────────────────────────────────────────

export function quatSet(out: Quat, x: number, y: number, z: number, w: number): Quat {
  out[0] = x;
  out[1] = y;
  out[2] = z;
  out[3] = w;
  return out;
}

export function quatCopy(src: QuatLike, out: Quat): Quat {
  out[0] = src[0];
  out[1] = src[1];
  out[2] = src[2];
  out[3] = src[3];
  return out;
}

/** out = a * b (apply b first, then a). `out` may alias a or b. */
export function quatMul(a: QuatLike, b: QuatLike, out: Quat): Quat {
  const ax = a[0], ay = a[1], az = a[2], aw = a[3];
  const bx = b[0], by = b[1], bz = b[2], bw = b[3];
  out[0] = aw * bx + ax * bw + ay * bz - az * by;
  out[1] = aw * by - ax * bz + ay * bw + az * bx;
  out[2] = aw * bz + ax * by - ay * bx + az * bw;
  out[3] = aw * bw - ax * bx - ay * by - az * bz;
  return out;
}

/** Rotation by `rad` about the unit axis (ax, ay, az) (right-handed). */
export function quatFromAxisAngle(ax: number, ay: number, az: number, rad: number, out: Quat): Quat {
  const h = rad / 2;
  const s = Math.sin(h);
  out[0] = ax * s;
  out[1] = ay * s;
  out[2] = az * s;
  out[3] = Math.cos(h);
  return out;
}

export function quatNormalize(q: Quat): Quat {
  const l = Math.sqrt(q[0] * q[0] + q[1] * q[1] + q[2] * q[2] + q[3] * q[3]);
  if (l === 0) return quatSet(q, 0, 0, 0, 1);
  const inv = 1 / l;
  q[0] *= inv;
  q[1] *= inv;
  q[2] *= inv;
  q[3] *= inv;
  return q;
}

export function quatConjugate(q: QuatLike, out: Quat): Quat {
  out[0] = -q[0];
  out[1] = -q[1];
  out[2] = -q[2];
  out[3] = q[3];
  return out;
}

/** Spherical interpolation along the shortest arc. `out` may alias a or b. */
export function quatSlerp(a: QuatLike, b: QuatLike, t: number, out: Quat): Quat {
  const ax = a[0], ay = a[1], az = a[2], aw = a[3];
  let bx = b[0], by = b[1], bz = b[2], bw = b[3];
  let cos = ax * bx + ay * by + az * bz + aw * bw;
  if (cos < 0) {
    cos = -cos;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let ka: number;
  let kb: number;
  if (cos > 0.9995) {
    ka = 1 - t;
    kb = t;
  } else {
    const theta = Math.acos(cos);
    const sin = Math.sin(theta);
    ka = Math.sin((1 - t) * theta) / sin;
    kb = Math.sin(t * theta) / sin;
  }
  out[0] = ka * ax + kb * bx;
  out[1] = ka * ay + kb * by;
  out[2] = ka * az + kb * bz;
  out[3] = ka * aw + kb * bw;
  return quatNormalize(out);
}

/** Rotate vector v by q: out = q v q^-1. */
export function quatRotateVec(q: QuatLike, vx: number, vy: number, vz: number, out: Vec3): Vec3 {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  // t = 2 * cross(q.xyz, v)
  const tx = 2 * (y * vz - z * vy);
  const ty = 2 * (z * vx - x * vz);
  const tz = 2 * (x * vy - y * vx);
  // v' = v + w t + cross(q.xyz, t)
  out[0] = vx + w * tx + (y * tz - z * ty);
  out[1] = vy + w * ty + (z * tx - x * tz);
  out[2] = vz + w * tz + (x * ty - y * tx);
  return out;
}

/** Angle (rad) of the rotation a^-1 b, in [0, pi]. */
export function quatAngleBetween(a: QuatLike, b: QuatLike): number {
  const d = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
  return 2 * Math.acos(d > 1 ? 1 : d);
}

/**
 * R_x(-90deg): sensor Earth frame (X east, Y magnetic north, Z up) -> three.js
 * WorldENU (X east, Y up, Z -north). q_world = Q_EARTH_TO_WORLD * q_sensor.
 */
export const Q_EARTH_TO_WORLD: Readonly<Quat> = [-Math.SQRT1_2, 0, 0, Math.SQRT1_2];

// ─── Orientation (device -> Earth) ───────────────────────────────────────────

const qA: Quat = [0, 0, 0, 1];
const qB: Quat = [0, 0, 0, 1];

/**
 * W3C DeviceOrientation Euler angles (intrinsic Z-X'-Y'': alpha about z,
 * beta about x', gamma about y'') -> device -> Earth quaternion.
 */
export function quatFromDeviceOrientation(alphaDeg: number, betaDeg: number, gammaDeg: number, out: Quat): Quat {
  quatFromAxisAngle(0, 0, 1, alphaDeg * DEG2RAD, qA);
  quatFromAxisAngle(1, 0, 0, betaDeg * DEG2RAD, qB);
  quatMul(qA, qB, qA);
  quatFromAxisAngle(0, 1, 0, gammaDeg * DEG2RAD, qB);
  return quatMul(qA, qB, out);
}

/**
 * Device -> Earth quaternion for a camera looking at `headingDeg` (clockwise
 * from north), `pitchDeg` above the horizon, rolled by `rollDeg` (clockwise as
 * seen by the user, i.e. the device's top tips to the right), in the device's
 * natural (portrait) orientation.
 */
export function quatFromHeadingPitchRoll(headingDeg: number, pitchDeg: number, rollDeg: number, out: Quat): Quat {
  // Upright portrait device facing north = R_x(+90deg) from lying flat.
  quatFromAxisAngle(0, 0, 1, -headingDeg * DEG2RAD, qA);
  quatFromAxisAngle(1, 0, 0, (90 + pitchDeg) * DEG2RAD, qB);
  quatMul(qA, qB, qA);
  quatFromAxisAngle(0, 0, 1, -rollDeg * DEG2RAD, qB);
  return quatMul(qA, qB, out);
}

export interface HeadingPitch {
  headingDeg: number;
  pitchDeg: number;
}

/**
 * Camera-forward (device -Z) heading and pitch of a device -> Earth quaternion
 * (Earth X east, Y north, Z up). `upAngleDeg` selects the screen-up axis used
 * near the vertical: device +Y rotated about device Z by -upAngleDeg (pass
 * screen.orientation.angle).
 */
export function earthHeadingPitch(q: QuatLike, upAngleDeg: number, out: HeadingPitch): HeadingPitch {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  // Columns of the rotation matrix = device axes in Earth coordinates.
  const c0x = 1 - 2 * (y * y + z * z), c0y = 2 * (x * y + w * z);
  const c1x = 2 * (x * y - w * z), c1y = 1 - 2 * (x * x + z * z);
  const fx = -2 * (x * z + w * y);
  const fy = -2 * (y * z - w * x);
  const fz = -(1 - 2 * (x * x + y * y));
  const a = upAngleDeg * DEG2RAD;
  const sa = Math.sin(a), ca = Math.cos(a);
  const ux = sa * c0x + ca * c1x;
  const uy = sa * c0y + ca * c1y;
  out.headingDeg = headingFromForwardUp(fx, fy, fz, ux, uy);
  out.pitchDeg = Math.asin(clamp(fz, -1, 1)) * RAD2DEG;
  return out;
}

/**
 * Same for Y-up frames (WorldENU and the WebXR 'local' space): heading is
 * measured clockwise from -Z (atan2(f.x, -f.z)), pitch against +Y.
 */
export function yUpHeadingPitch(q: QuatLike, upAngleDeg: number, out: HeadingPitch): HeadingPitch {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  const c0x = 1 - 2 * (y * y + z * z), c0z = 2 * (x * z - w * y);
  const c1x = 2 * (x * y - w * z), c1z = 2 * (y * z + w * x);
  const fx = -2 * (x * z + w * y);
  const fy = -2 * (y * z - w * x);
  const fz = -(1 - 2 * (x * x + y * y));
  const a = upAngleDeg * DEG2RAD;
  const sa = Math.sin(a), ca = Math.cos(a);
  const ux = sa * c0x + ca * c1x;
  const uz = sa * c0z + ca * c1z;
  out.headingDeg = headingFromForwardUp(fx, -fz, fy, ux, -uz);
  out.pitchDeg = Math.asin(clamp(fy, -1, 1)) * RAD2DEG;
  return out;
}

// ─── WGS84 ───────────────────────────────────────────────────────────────────

const WGS84_A = 6378137;
const WGS84_F = 1 / 298.257223563;
const WGS84_E2 = WGS84_F * (2 - WGS84_F);

/** Meridian radius of curvature M (m) at latitude (deg). */
export function meridianRadius(latDeg: number): number {
  const s = Math.sin(latDeg * DEG2RAD);
  const w = 1 - WGS84_E2 * s * s;
  return (WGS84_A * (1 - WGS84_E2)) / (w * Math.sqrt(w));
}

/** Prime-vertical radius of curvature N (m) at latitude (deg). */
export function primeVerticalRadius(latDeg: number): number {
  const s = Math.sin(latDeg * DEG2RAD);
  return WGS84_A / Math.sqrt(1 - WGS84_E2 * s * s);
}

/** Metres per degree of latitude / longitude at latDeg. */
export function metresPerDegree(latDeg: number, out: { lat: number; lon: number }): { lat: number; lon: number } {
  out.lat = meridianRadius(latDeg) * DEG2RAD;
  out.lon = primeVerticalRadius(latDeg) * Math.cos(latDeg * DEG2RAD) * DEG2RAD;
  return out;
}

// ─── Random ──────────────────────────────────────────────────────────────────

/** Standard normal sample (Box-Muller) from a uniform [0, 1) source. */
export function gaussian(random: () => number): number {
  let u = random();
  while (u <= 1e-12) u = random();
  const v = random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Deterministic PRNG (mulberry32) for tests and reproducible mocks. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
